import AiAnalyticsApi from '../api/ai/AiAnalyticsApi';
import { threadDeleteErrorMessage } from '../components/pages/AiAnalytics/AiAnalyticsChat.service';
import { messageIdOf, previewToMessageChanges } from '../components/pages/AiAnalytics/services/aiDataUtils';
import { buildTableSpec } from '../components/pages/AiAnalytics/services/chartTypeUtils';
import {
  ASK_OUTCOMES,
  ASK_PHASES,
  ASK_POLL_DEADLINE_MS,
  ASK_PROCESS_FAILED_MESSAGE,
  ASK_TIMED_OUT_MESSAGE,
  CHART_FAILED_FALLBACK_MESSAGE,
  CHART_MAX_POLL_ATTEMPTS,
  CHART_POLL_INTERVAL_MS,
  DEFAULT_THREAD_TITLE,
  FEEDBACK_FAILED_MESSAGE,
  MESSAGE_ROLES,
  OP_KINDS,
  OP_RESUME_DEADLINE_MS,
  OP_RESUME_POLL_MS,
  OP_RUNNING_LABELS
} from '../constants/aiAnalytics';
import { activeThreadContext, chartContext, resumedOpTitle } from '../services/aiAnalytics/aiSelectors';
import {
  buildAskFailureMessage,
  buildAskNoSqlMessage,
  buildAskResultMessage,
  classifyAskTask,
  delay,
  deriveThreadTitle,
  diffAskProgress,
  hasGeneratedSql,
  isRetryablePollError,
  nextAskPollDelay,
  pollUntil,
  registerPoll,
  releasePoll,
  serverAuthoredMessageId,
  shouldAutoTitle
} from '../services/aiAnalytics/askPolling.service';
import {
  chartFailureMessage,
  normalizeChartSpec,
  resolveChartPollResult
} from '../services/aiAnalytics/chartPolling.service';
import {
  acquireResumedOpsWatch,
  chartOpKey,
  finishedResumedOps,
  messageOpKey,
  operationKey,
  opOutcomeLabel,
  releaseResumedOpsWatch,
  resumedOpDescriptor
} from '../services/aiAnalytics/operationResume.service';
import { onRequestError } from '../services/handlers';

import {
  ADD_AI_ANALYTICS_COMPLETED_ASK,
  ADD_AI_ANALYTICS_MESSAGE,
  ADD_SAVED_CHART,
  CLEAR_AI_ANALYTICS_COMPLETED_ASKS,
  DISMISS_AI_ANALYTICS_ASK_POPUP,
  REMOVE_AI_ANALYTICS_COMPLETED_ASK,
  REMOVE_SAVED_CHART,
  RESET_AI_ANALYTICS_ASK_POPUP_DISMISSALS,
  SET_AI_ANALYTICS_ACTIVE_THREAD,
  SET_AI_ANALYTICS_ERROR,
  SET_AI_ANALYTICS_PENDING_ASK,
  SET_AI_ANALYTICS_PENDING_OP,
  SET_AI_ANALYTICS_THREADS,
  SET_AI_ANALYTICS_THREADS_LOADING,
  SET_SAVED_CHARTS,
  SET_SELECTED_CHART,
  UPDATE_AI_ANALYTICS_MESSAGE,
  UPDATE_AI_ANALYTICS_THREAD,
  UPDATE_SAVED_CHART
} from './aiAnalyticsActionTypes';

function clearError(dispatch) {
  dispatch({ type: SET_AI_ANALYTICS_ERROR, payload: null });
}

function reportError(dispatch, error, message) {
  dispatch({ type: SET_AI_ANALYTICS_ERROR, payload: message });
  onRequestError(error, { customMessage: message });
}

function setThreadsLoading(dispatch, loading) {
  dispatch({ type: SET_AI_ANALYTICS_THREADS_LOADING, payload: loading });
}

function setPendingAsk(dispatch, threadId, ask) {
  dispatch({ type: SET_AI_ANALYTICS_PENDING_ASK, payload: { threadId, ask } });
}

function beginOp(dispatch, getState, key, kind, label, context) {
  if (getState().aiAnalytics.pendingOps[key]) return false;
  dispatch({ type: SET_AI_ANALYTICS_PENDING_OP, payload: { key, op: { key, kind, label, ...context } } });
  return true;
}

function finishOp(dispatch, getState, key, outcome) {
  const op = getState().aiAnalytics.pendingOps[key];
  if (!op) return;
  dispatch({
    type: ADD_AI_ANALYTICS_COMPLETED_ASK,
    payload: { queryId: key, threadTitle: op.title, outcome, doneLabel: opOutcomeLabel(outcome, op.kind) }
  });
  dispatch({ type: SET_AI_ANALYTICS_PENDING_OP, payload: { key, op: null } });
}

export function fetchThreads() {
  return async function(dispatch) {
    try {
      const response = await AiAnalyticsApi.getThreads();
      const threads = response.data || [];
      dispatch({ type: SET_AI_ANALYTICS_THREADS, payload: threads });
      threads
        .filter(thread => thread.activeAsk?.queryId)
        .forEach(thread => dispatch(resumeActiveAsk(thread.id, thread.activeAsk.queryId)));
    } catch (error) {
      reportError(dispatch, error, 'Failed to fetch threads');
    }
  };
}

export function createThread() {
  return async function(dispatch) {
    try {
      clearError(dispatch);
      setThreadsLoading(dispatch, true);
      const response = await AiAnalyticsApi.createThread(DEFAULT_THREAD_TITLE);
      const thread = { id: response.data.id, title: response.data.title, favorite: false, messages: [] };
      dispatch({ type: SET_AI_ANALYTICS_ACTIVE_THREAD, payload: thread });
      dispatch(fetchThreads());
      return thread;
    } catch (error) {
      reportError(dispatch, error, 'Failed to create thread');
    } finally {
      setThreadsLoading(dispatch, false);
    }
  };
}

export function renameThread(threadId, title) {
  return async function(dispatch) {
    try {
      const response = await AiAnalyticsApi.updateThread(threadId, { title });
      dispatch({ type: UPDATE_AI_ANALYTICS_THREAD, payload: response.data });
      return response.data;
    } catch (error) {
      reportError(dispatch, error, 'Failed to rename thread');
    }
  };
}

export function favoriteThread(threadId, favorite) {
  return async function(dispatch) {
    try {
      const response = await AiAnalyticsApi.updateThread(threadId, { favorite });
      dispatch({ type: UPDATE_AI_ANALYTICS_THREAD, payload: response.data });
      return response.data;
    } catch (error) {
      reportError(dispatch, error, 'Failed to update favorite');
    }
  };
}

async function reconnectActiveAsk(dispatch, threadId) {
  try {
    const active = await AiAnalyticsApi.getActiveAsk(threadId);
    if (active?.data?.queryId) dispatch(resumeActiveAsk(threadId, active.data.queryId));
  } catch (error) {}
}

export function loadThread(threadId) {
  return async function(dispatch) {
    try {
      setThreadsLoading(dispatch, true);
      const response = await AiAnalyticsApi.getThread(threadId);
      const thread = response.data;
      dispatch({
        type: SET_AI_ANALYTICS_ACTIVE_THREAD,
        payload: { id: thread.id, title: thread.title, favorite: thread.favorite, messages: thread.messages || [] }
      });
      await reconnectActiveAsk(dispatch, threadId);
    } catch (error) {
      reportError(dispatch, error, 'Failed to load thread');
    } finally {
      setThreadsLoading(dispatch, false);
    }
  };
}

export function closeActiveThread() {
  return { type: SET_AI_ANALYTICS_ACTIVE_THREAD, payload: null };
}

export function deleteThread(threadId) {
  return async function(dispatch, getState) {
    try {
      const pendingAsk = getState().aiAnalytics.pendingAsks[threadId];
      await AiAnalyticsApi.deleteThread(threadId);
      if (pendingAsk?.queryId) releasePoll(pendingAsk.queryId);
      setPendingAsk(dispatch, threadId, null);
      dispatch(fetchThreads());
      dispatch({ type: SET_AI_ANALYTICS_ACTIVE_THREAD, payload: null });
    } catch (error) {
      reportError(dispatch, error, threadDeleteErrorMessage(error));
    }
  };
}

async function fetchPreviewData(messageId) {
  try {
    return (await AiAnalyticsApi.previewData(messageId)).data;
  } catch (error) {
    return null;
  }
}

async function drivePendingAsk(dispatch, getState, threadId, queryId) {
  if (!registerPoll(queryId)) return null;

  const onActiveThread = () => getState().aiAnalytics.activeThread?.id === threadId;
  const addMessage = payload => {
    if (!onActiveThread()) return;
    const alreadyShown =
      payload.messageId &&
      (getState().aiAnalytics.activeThread.messages || []).some(message => messageIdOf(message) === payload.messageId);
    if (!alreadyShown) dispatch({ type: ADD_AI_ANALYTICS_MESSAGE, payload });
  };
  const applyTaskProgress = task => {
    const changes = diffAskProgress(task, getState().aiAnalytics.pendingAsks[threadId]);
    if (Object.keys(changes).length) setPendingAsk(dispatch, threadId, changes);
  };

  let outcome = ASK_OUTCOMES.FAILED;
  try {
    const seeded = getState().aiAnalytics.pendingAsks[threadId] || {};
    const threadTitle = seeded.threadTitle || getState().aiAnalytics.threads.find(t => t.id === threadId)?.title;
    setPendingAsk(dispatch, threadId, { queryId, phase: seeded.phase || ASK_PHASES.PENDING, threadTitle });

    const settledPoll = await pollUntil({
      poll: async () => {
        const task = (await AiAnalyticsApi.pollAskResult(queryId)).data;
        applyTaskProgress(task);
        return task;
      },
      classify: classifyAskTask,
      delayFor: nextAskPollDelay,
      deadlineMs: ASK_POLL_DEADLINE_MS,
      isRetryableError: isRetryablePollError
    });

    if (settledPoll.timedOut) {
      addMessage({ role: MESSAGE_ROLES.ASSISTANT, content: ASK_TIMED_OUT_MESSAGE, isError: true });
      return null;
    }

    const task = settledPoll.result;
    if (settledPoll.outcome !== ASK_OUTCOMES.FINISHED) {
      outcome = settledPoll.outcome;
      addMessage(buildAskFailureMessage(task));
      return null;
    }

    if (!hasGeneratedSql(task)) {
      addMessage(buildAskNoSqlMessage());
      return null;
    }

    const preview = await fetchPreviewData(serverAuthoredMessageId(task));
    outcome = ASK_OUTCOMES.FINISHED;
    const resultMessage = buildAskResultMessage(task, preview);
    addMessage(resultMessage);

    return {
      sql: resultMessage.sql,
      data: resultMessage.data,
      columns: resultMessage.columns,
      messageId: resultMessage.messageId
    };
  } catch (error) {
    const errorMessage = error.message || ASK_PROCESS_FAILED_MESSAGE;
    if (onActiveThread()) dispatch({ type: SET_AI_ANALYTICS_ERROR, payload: errorMessage });
    addMessage({ role: MESSAGE_ROLES.ASSISTANT, content: `Error: ${errorMessage}`, isError: true });
    return null;
  } finally {
    releasePoll(queryId);
    const settled = getState().aiAnalytics.pendingAsks[threadId];
    if (settled?.queryId === queryId) {
      dispatch({
        type: ADD_AI_ANALYTICS_COMPLETED_ASK,
        payload: { queryId, threadId, threadTitle: settled.threadTitle, question: settled.question, outcome }
      });
      setPendingAsk(dispatch, threadId, null);
    }
  }
}

function askPayload(question, refinedFromMessageId) {
  return refinedFromMessageId ? { question, refinedFromMessageId } : { question };
}

export function askQuestion(threadId, question, refinedFromMessageId) {
  return async function(dispatch, getState) {
    clearError(dispatch);
    const activeThread = getState().aiAnalytics.activeThread;
    let threadTitle = activeThread?.title;
    if (shouldAutoTitle(activeThread)) {
      threadTitle = deriveThreadTitle(question);
      dispatch(renameThread(threadId, threadTitle));
    }

    dispatch({ type: ADD_AI_ANALYTICS_MESSAGE, payload: { role: MESSAGE_ROLES.USER, content: question } });
    setPendingAsk(dispatch, threadId, { phase: ASK_PHASES.PENDING, question, threadTitle });

    try {
      const askResponse = await AiAnalyticsApi.askQuestion(threadId, askPayload(question, refinedFromMessageId));
      return await drivePendingAsk(dispatch, getState, threadId, askResponse.data.queryId);
    } catch (error) {
      setPendingAsk(dispatch, threadId, null);
      const errorMessage = error.message || ASK_PROCESS_FAILED_MESSAGE;
      dispatch({ type: SET_AI_ANALYTICS_ERROR, payload: errorMessage });
      dispatch({
        type: ADD_AI_ANALYTICS_MESSAGE,
        payload: { role: MESSAGE_ROLES.ASSISTANT, content: `Error: ${errorMessage}`, isError: true }
      });
      return null;
    }
  };
}

export function resumeActiveAsk(threadId, queryId) {
  return function(dispatch, getState) {
    return drivePendingAsk(dispatch, getState, threadId, queryId);
  };
}

async function applyResumedOpResult(dispatch, getState, op) {
  try {
    if (op.chartId) {
      const response = await AiAnalyticsApi.getCharts();
      const fresh = (response.data || []).find(chart => chart.id === op.chartId);
      if (fresh) dispatch({ type: UPDATE_SAVED_CHART, payload: fresh });
    } else if (getState().aiAnalytics.activeThread?.id !== op.threadId) {
      return;
    } else if (op.kind === OP_KINDS.REFRESH_DATA) {
      const preview = (await AiAnalyticsApi.previewData(op.messageId)).data;
      dispatch({
        type: UPDATE_AI_ANALYTICS_MESSAGE,
        payload: { messageId: op.messageId, changes: previewToMessageChanges(preview) }
      });
    } else {
      const detail = (await AiAnalyticsApi.getThread(op.threadId)).data;
      const fresh = (detail.messages || []).find(message => messageIdOf(message) === op.messageId);
      if (fresh?.chartSpec) {
        dispatch({
          type: UPDATE_AI_ANALYTICS_MESSAGE,
          payload: { messageId: op.messageId, changes: { chartSpec: fresh.chartSpec } }
        });
      }
    }
  } catch (error) {}
}

function resumedOps(getState) {
  return Object.values(getState().aiAnalytics.pendingOps).filter(op => op.resumed);
}

function finishResumedOpsAsFailed(dispatch, getState) {
  resumedOps(getState).forEach(op => finishOp(dispatch, getState, op.key, ASK_OUTCOMES.FAILED));
}

async function watchResumedOps(dispatch, getState) {
  if (!acquireResumedOpsWatch()) return;
  try {
    const startedAt = Date.now();
    for (;;) {
      if (!resumedOps(getState).length) return;
      if (Date.now() - startedAt >= OP_RESUME_DEADLINE_MS) {
        finishResumedOpsAsFailed(dispatch, getState);
        return;
      }
      await delay(OP_RESUME_POLL_MS);

      let activeKeys;
      try {
        activeKeys = new Set(((await AiAnalyticsApi.getActiveOperations()).data || []).map(operationKey));
      } catch (error) {
        if (isRetryablePollError(error)) continue;
        finishResumedOpsAsFailed(dispatch, getState);
        return;
      }

      const finished = finishedResumedOps(getState().aiAnalytics.pendingOps, activeKeys);
      for (const op of finished) {
        await applyResumedOpResult(dispatch, getState, op);
        const title = op.title || resumedOpTitle(getState(), op);
        if (title && !op.title) {
          dispatch({ type: SET_AI_ANALYTICS_PENDING_OP, payload: { key: op.key, op: { ...op, title } } });
        }
        finishOp(dispatch, getState, op.key, ASK_OUTCOMES.FINISHED);
      }
    }
  } finally {
    releaseResumedOpsWatch();
  }
}

export function resumePendingOperations() {
  return async function(dispatch, getState) {
    let operations;
    try {
      operations = (await AiAnalyticsApi.getActiveOperations()).data || [];
    } catch (error) {
      return;
    }
    operations.forEach(operation => {
      const { key, kind, label, ...context } = resumedOpDescriptor(operation, getState());
      beginOp(dispatch, getState, key, kind, label, context);
    });
    if (operations.length) watchResumedOps(dispatch, getState);
  };
}

export function dismissAskPopup(queryId) {
  return { type: DISMISS_AI_ANALYTICS_ASK_POPUP, payload: queryId };
}

export function resetAskPopupDismissals() {
  return { type: RESET_AI_ANALYTICS_ASK_POPUP_DISMISSALS };
}

export function removeCompletedAskPopup(queryId) {
  return { type: REMOVE_AI_ANALYTICS_COMPLETED_ASK, payload: queryId };
}

export function clearCompletedAskPopups() {
  return { type: CLEAR_AI_ANALYTICS_COMPLETED_ASKS };
}

async function pollChartAndPersist(dispatch, messageId, queryId) {
  const settledPoll = await pollUntil({
    poll: async () => (await AiAnalyticsApi.pollChartResult(queryId)).data,
    classify: resolveChartPollResult,
    delayFor: () => CHART_POLL_INTERVAL_MS,
    maxAttempts: CHART_MAX_POLL_ATTEMPTS
  });

  if (settledPoll.timedOut) return null;
  if (settledPoll.outcome === ASK_OUTCOMES.FAILED) throw new Error(chartFailureMessage(settledPoll.result));

  const spec = settledPoll.result.response.chart_schema;
  const specJson = normalizeChartSpec(spec);
  dispatch({ type: UPDATE_AI_ANALYTICS_MESSAGE, payload: { messageId, changes: { chartSpec: specJson } } });
  return spec;
}

function runChartTask(messageId, kind, startTask) {
  return async function(dispatch, getState) {
    clearError(dispatch);
    const key = messageOpKey(messageId);
    if (!beginOp(dispatch, getState, key, kind, OP_RUNNING_LABELS[kind], activeThreadContext(getState()))) {
      return null;
    }
    let outcome = ASK_OUTCOMES.FAILED;
    try {
      const chartResponse = await startTask();
      const spec = await pollChartAndPersist(dispatch, messageId, chartResponse.data.queryId);
      outcome = spec ? ASK_OUTCOMES.FINISHED : ASK_OUTCOMES.FAILED;
      return spec;
    } catch (error) {
      reportError(dispatch, error, error.message || CHART_FAILED_FALLBACK_MESSAGE);
      return null;
    } finally {
      finishOp(dispatch, getState, key, outcome);
    }
  };
}

export function generateChart(messageId) {
  return runChartTask(messageId, OP_KINDS.GENERATE_CHART, () => AiAnalyticsApi.generateChart(messageId));
}

export function refineChart(messageId, instruction) {
  return runChartTask(messageId, OP_KINDS.REFINE_CHART, () => AiAnalyticsApi.refineChart(messageId, instruction));
}

export function loadMessageData(messageId) {
  return async function(dispatch) {
    try {
      const response = await AiAnalyticsApi.previewData(messageId);
      return response.data;
    } catch (error) {
      reportError(dispatch, error, 'Failed to load the dataset');
      return null;
    }
  };
}

export function refreshMessageData(messageId) {
  return async function(dispatch, getState) {
    clearError(dispatch);
    const key = messageOpKey(messageId);
    const kind = OP_KINDS.REFRESH_DATA;
    if (!beginOp(dispatch, getState, key, kind, OP_RUNNING_LABELS[kind], activeThreadContext(getState()))) {
      return null;
    }
    let outcome = ASK_OUTCOMES.FAILED;
    try {
      const response = await AiAnalyticsApi.refreshMessageData(messageId);
      const preview = response.data || {};
      dispatch({
        type: UPDATE_AI_ANALYTICS_MESSAGE,
        payload: { messageId, changes: previewToMessageChanges(preview) }
      });
      outcome = ASK_OUTCOMES.FINISHED;
      return preview;
    } catch (error) {
      reportError(dispatch, error, 'Failed to refresh data');
      return null;
    } finally {
      finishOp(dispatch, getState, key, outcome);
    }
  };
}

export function setMessageChartSpec(messageId, chartSpec) {
  return async function(dispatch) {
    clearError(dispatch);
    try {
      await AiAnalyticsApi.saveMessageChartSpec(messageId, chartSpec);
    } catch (error) {
      reportError(dispatch, error, 'Failed to save chart view');
    }
    dispatch({ type: UPDATE_AI_ANALYTICS_MESSAGE, payload: { messageId, changes: { chartSpec } } });
  };
}

export function rateMessage(messageId, rating, comment) {
  return async function(dispatch) {
    clearError(dispatch);
    try {
      if (!rating) {
        await AiAnalyticsApi.removeMessageFeedback(messageId);
        dispatch({ type: UPDATE_AI_ANALYTICS_MESSAGE, payload: { messageId, changes: { feedback: null } } });
        return true;
      }
      const { data } = await AiAnalyticsApi.rateMessage(messageId, { rating, comment });
      dispatch({ type: UPDATE_AI_ANALYTICS_MESSAGE, payload: { messageId, changes: { feedback: data } } });
      return data;
    } catch (error) {
      reportError(dispatch, error, FEEDBACK_FAILED_MESSAGE);
      return null;
    }
  };
}

export function selectChart(chart) {
  return { type: SET_SELECTED_CHART, payload: chart };
}

export function clearSelectedChart() {
  return { type: SET_SELECTED_CHART, payload: null };
}

export function saveChart(data) {
  return async function(dispatch) {
    try {
      clearError(dispatch);
      const response = await AiAnalyticsApi.saveChart(data);
      dispatch({ type: ADD_SAVED_CHART, payload: response.data });
      return response.data;
    } catch (error) {
      reportError(dispatch, error, 'Failed to save chart');
    }
  };
}

export function regenerateChartType(chartId, type) {
  return async function(dispatch, getState) {
    clearError(dispatch);
    const key = chartOpKey(chartId);
    const kind = OP_KINDS.GENERATE_CHART;
    if (!beginOp(dispatch, getState, key, kind, OP_RUNNING_LABELS[kind], chartContext(getState(), chartId))) {
      return null;
    }
    let outcome = ASK_OUTCOMES.FAILED;
    try {
      const response = await AiAnalyticsApi.regenerateChart(chartId, type);
      dispatch({ type: UPDATE_SAVED_CHART, payload: response.data });
      outcome = ASK_OUTCOMES.FINISHED;
      return response.data;
    } catch (error) {
      reportError(dispatch, error, 'Failed to change chart type');
      return null;
    } finally {
      finishOp(dispatch, getState, key, outcome);
    }
  };
}

export function setSavedChartToTable(chartId) {
  return async function(dispatch) {
    try {
      clearError(dispatch);
      const response = await AiAnalyticsApi.updateChart(chartId, { vegaSpec: buildTableSpec() });
      dispatch({ type: UPDATE_SAVED_CHART, payload: response.data });
      return response.data;
    } catch (error) {
      reportError(dispatch, error, 'Failed to switch chart to table');
      return null;
    }
  };
}

export function deleteChart(chartId) {
  return async function(dispatch) {
    try {
      await AiAnalyticsApi.deleteChart(chartId);
      dispatch({ type: REMOVE_SAVED_CHART, payload: chartId });
    } catch (error) {
      reportError(dispatch, error, 'Failed to delete chart');
    }
  };
}

export function fetchCharts() {
  return async function(dispatch) {
    try {
      const response = await AiAnalyticsApi.getCharts();
      dispatch({ type: SET_SAVED_CHARTS, payload: response.data || [] });
    } catch (error) {
      reportError(dispatch, error, 'Failed to fetch charts');
    }
  };
}

export function refreshChartData(chartId) {
  return async function(dispatch, getState) {
    clearError(dispatch);
    const key = chartOpKey(chartId);
    const kind = OP_KINDS.REFRESH_DATA;
    if (!beginOp(dispatch, getState, key, kind, OP_RUNNING_LABELS[kind], chartContext(getState(), chartId))) {
      return null;
    }
    let outcome = ASK_OUTCOMES.FAILED;
    try {
      const response = await AiAnalyticsApi.refreshChartData(chartId);
      const result = response.data || {};
      dispatch({
        type: UPDATE_SAVED_CHART,
        payload: {
          id: chartId,
          lastRefreshedData: JSON.stringify(result),
          lastRefreshedAt: result.executedAt ?? new Date().toISOString()
        }
      });
      outcome = ASK_OUTCOMES.FINISHED;
      return result;
    } catch (error) {
      reportError(dispatch, error, 'Failed to refresh chart data');
      return null;
    } finally {
      finishOp(dispatch, getState, key, outcome);
    }
  };
}
